/**
 * Measurement at run time, for the pages the app draws itself (the watch page).
 * The register page gets the same tags written into its head by the server
 * (shared/measure.ts); here they are loaded once into the document, and the
 * events the pages agreed on are sent through them. Nothing here runs unless
 * the workspace set an id.
 */
import { GA_ID_RE, PIXEL_ID_RE } from "../../shared/measure";
import { isStandardMetaEvent, type MetaContent } from "../../shared/meta-events";
import type { MeasurementConfig } from "../../shared/types";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean };
    _fbq?: unknown;
  }
}

/** Marked as the team's own browser, or asked for DebugView, on this page. */
export function measureFlags(): { internal: boolean; debug: boolean } {
  try {
    const q = window.location.search || "";
    if (/[?&]internal=1(&|$)/.test(q)) {
      const h = window.location.hostname.split(".");
      const top = h.slice(-2).join(".");
      const domain = h.length >= 3 && top !== "flashsuite.net" ? `; domain=.${top}` : "";
      document.cookie = `fr_internal=1; path=/; max-age=31536000; SameSite=Lax; Secure${domain}`;
    }
    return {
      internal: /(^|; )[A-Za-z0-9]*_?internal=1(;|$)/.test(document.cookie),
      debug: /[?&]debug=1(&|$)/.test(q),
    };
  } catch {
    return { internal: false, debug: false };
  }
}

const gtagSrc = (id: string) => `https://www.googletagmanager.com/gtag/js?id=${id}`;

/**
 * Load the tags once. Safe to call again: the document itself says what is
 * already there, so a second call (or a second mount) does nothing. The pixel's
 * PageView names the webinar the page is about.
 */
export function installMeasurement(m: MeasurementConfig, content?: MetaContent): void {
  if (typeof document === "undefined") return;
  const flags = measureFlags();
  const ga = m.ga_measurement_id && GA_ID_RE.test(m.ga_measurement_id) ? m.ga_measurement_id : null;
  const px = m.meta_pixel_id && PIXEL_ID_RE.test(m.meta_pixel_id) ? m.meta_pixel_id : null;
  if (ga && !document.querySelector(`script[src="${gtagSrc(ga)}"]`)) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // biome-ignore lint/style/noArguments: the Google tag reads `arguments` itself.
      window.dataLayer?.push(arguments);
    };
    window.gtag("js", new Date());
    const cfg: Record<string, unknown> = {};
    if (flags.internal) cfg.traffic_type = "internal";
    if (flags.debug) cfg.debug_mode = true;
    window.gtag("config", ga, cfg);
    const s = document.createElement("script");
    s.async = true;
    s.src = gtagSrc(ga);
    document.head.appendChild(s);
  }
  // The team's own browsers send Meta nothing, from the pages or from our server.
  if (px && !flags.internal && !document.querySelector(`meta[name="fr-pixel"][content="${px}"]`)) {
    const mark = document.createElement("meta");
    mark.name = "fr-pixel";
    mark.content = px;
    document.head.appendChild(mark);
    if (!window.fbq) {
      const n = function fbq(...args: unknown[]) {
        const self = n as typeof n & { callMethod?: (...a: unknown[]) => void; queue: unknown[] };
        if (self.callMethod) self.callMethod(...args);
        else self.queue.push(args);
      } as ((...args: unknown[]) => void) & { queue: unknown[]; loaded: boolean; version: string; push: unknown };
      n.queue = [];
      n.loaded = true;
      n.version = "2.0";
      n.push = n;
      window.fbq = n;
      window._fbq = n;
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.appendChild(s);
    }
    window.fbq?.("init", px);
    if (content) window.fbq?.("track", "PageView", content);
    else window.fbq?.("track", "PageView");
  }
}

/** One Analytics event, when the tag is there. */
export function gaEvent(name: string, params: Record<string, unknown>): void {
  window.gtag?.("event", name, params);
}

/**
 * One Meta event, when the pixel is there. The id is the one our server sends
 * the same moment with, so Meta counts it once. Meta's own event names go
 * through `track`, ours through `trackCustom`.
 */
export function metaEvent(name: string, params: Record<string, unknown>, eventId: string): void {
  if (!window.fbq) return;
  window.fbq(isStandardMetaEvent(name) ? "track" : "trackCustom", name, params, { eventID: eventId });
}

/** The talk's milestones, sent once per view each, in Analytics' own names. */
interface VideoMeasure {
  title: string;
  url: string | null;
  durationSec: number;
}

const MILESTONES = [10, 25, 50, 75] as const;

/**
 * Where a video is in its measurement: what has been sent. Pure, so the watch
 * page can feed it playhead ticks and read back what to send.
 */
interface VideoSent {
  started: boolean;
  milestones: Set<number>;
  completed: boolean;
}

export function freshVideoSent(): VideoSent {
  return { started: false, milestones: new Set(), completed: false };
}

/**
 * Which events one playing step earns, in order: a start, the milestones the
 * step crossed, the end. Seeking earns nothing: only a playing step counts.
 */
export function videoEventsFor(
  sent: VideoSent,
  step: { to: number; playing: boolean },
  durationSec: number,
): Array<{ name: "video_start" | "video_progress" | "video_complete"; percent: number }> {
  const out: Array<{ name: "video_start" | "video_progress" | "video_complete"; percent: number }> = [];
  if (!step.playing || !(durationSec > 0)) return out;
  const pct = Math.max(0, Math.min(100, (step.to / durationSec) * 100));
  if (!sent.started && step.to > 0) {
    sent.started = true;
    out.push({ name: "video_start", percent: 0 });
  }
  for (const m of MILESTONES) {
    if (pct >= m && !sent.milestones.has(m)) {
      sent.milestones.add(m);
      out.push({ name: "video_progress", percent: m });
    }
  }
  if (!sent.completed && (pct >= 99.5 || step.to >= durationSec - 1)) {
    sent.completed = true;
    out.push({ name: "video_complete", percent: 100 });
  }
  return out;
}

/** The parameters every video event carries, in Analytics' own names. */
export function videoParams(v: VideoMeasure, currentSec: number, percent: number): Record<string, unknown> {
  return {
    video_title: v.title,
    video_url: v.url ?? "",
    video_provider: "flashroom",
    video_duration: Math.round(v.durationSec),
    video_current_time: Math.round(currentSec),
    video_percent: percent,
  };
}
